import { dehydrate, HydrationBoundary } from '@tanstack/react-query';
import { Suspense } from 'react';

import { cn } from '@natu/utils/cn';

import { getQueryClient } from '../../../../../../getQueryClient';
import {
  getLinkPropsFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  SBProps,
} from '../../../../../utils';
import { getArticlesByFilters } from '../../../utils/getArticlesByFilters';
import { ArticlesByFiltersContent } from './components/ArticlesByFiltersContent';
import { ArticlesByFiltersSkeleton } from './components/ArticlesByFiltersSkeleton';
import { SBArticlesByFiltersProps } from './types';

export const SBArticlesByFilters = ({ blok }: SBProps<SBArticlesByFiltersProps>) => {
  const {
    author,
    category,
    mbDesktop,
    mbMobile,
    mbTablet,
    mtDesktop,
    mtMobile,
    mtTablet,
    heading,
    allPostsLabel,
    allPostsLink,
    parentProps,
    recommendedArticles,
    withPagination,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const page = parentProps?.page ? Number(parentProps.page) : 1;

  const queryClient = getQueryClient();
  queryClient.prefetchQuery({
    queryKey: ['articles', { filters: { author, category }, page, draft: false }],
    queryFn: async () =>
      getArticlesByFilters({
        filters: { author, category },
        page,
        draftMode: false,
      }),
  });

  const resolvedAllPostsLink = getLinkPropsFromStoryblok(allPostsLink);

  return (
    <div
      className={cn('mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8', className)}
      {...sbEditable(blok)}
    >
      <HydrationBoundary state={dehydrate(queryClient)}>
        <Suspense fallback={<ArticlesByFiltersSkeleton />}>
          <ArticlesByFiltersContent
            resolvedAllPostsLink={resolvedAllPostsLink}
            allPostsLabel={allPostsLabel}
            author={author}
            category={category}
            heading={heading}
            page={page}
            withPagination={withPagination}
            recommendedArticles={recommendedArticles}
          />
        </Suspense>
      </HydrationBoundary>
    </div>
  );
};
